import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Check, X, ChevronDown, Search, Lock, Settings2, SlidersHorizontal } from 'lucide-react';
import { DateSlicerControl } from './DateSlicerControl';

// Clamp a right-click menu to stay fully on-screen (menu ~176px wide).
const menuPos = (ctx) => {
  const w = 176, h = 320;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  return { left: Math.max(8, Math.min(ctx.x, vw - w - 8)), top: Math.max(8, Math.min(ctx.y, vh - h - 8)) };
};


const DISPLAY_MODES = [
  { key: 'dropdown', label: 'Dropdown' },
  { key: 'multi', label: 'Multi-Select' },
  { key: 'buttons', label: 'Buttons' },
  { key: 'radio', label: 'Radio List' },
  { key: 'search', label: 'Searchable' },
  { key: 'text_match', label: 'Text filter' },
  { key: 'date_picker', label: 'Date Picker' },
  { key: 'date_range', label: 'Date Range' },
  { key: 'number_range', label: 'Number Range' },
];

export function SlicerControl({ dimension, label: labelProp, values, selected, onChange: onChangeProp, displayMode: externalMode, onDisplayModeChange, isDate = false, fiscalStartMonth = 1, settings = null, onOpenSettings = null, onFieldProperties = null }) {
  const [ctxMenu, setCtxMenu] = useState(null);
  // Effective display mode is derived reactively so that when `isDate` resolves
  // true only after the model's fields load asynchronously, a date field still
  // upgrades to the calendar. `modeOverride` holds an explicit right-click pick.
  const [modeOverride, setModeOverride] = useState(null);
  // A pre-seeded numeric-range value ({nk:'num'}) forces the number-range editor
  // so an Explore amount/range filter renders as an editable min/max control.
  const isNumRangeVal = selected && typeof selected === 'object' && selected.nk === 'num';
  // Per-slicer author settings (display/selection/All/lock/title) take priority.
  const locked = !!(settings && settings.locked);
  const effShowAll = settings ? settings.showAll !== false : true;
  const wantMulti = settings && settings.selectionMode === 'multiple';
  const settingMode = settings && settings.displayType ? settings.displayType : null;
  let mode = modeOverride || settingMode || (isNumRangeVal ? 'number_range' : (externalMode || (isDate ? 'calendar' : 'dropdown')));
  if (wantMulti && !modeOverride && ['dropdown', 'buttons', 'radio', 'search'].includes(mode)) mode = 'multi';
  // Disable all mutations when the author locked this slicer — but only for
  // viewers. In the builder (where onOpenSettings is wired) the author can
  // always change a locked slicer.
  const enforceLock = locked && !onOpenSettings;
  const onChange = enforceLock ? (() => {}) : onChangeProp;
  const [multiSelected, setMultiSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [multiOpen, setMultiOpen] = useState(false);

  const label = (settings && settings.customTitle) || labelProp || dimension.replace(/_/g, ' ');

  // Date fields default to the calendar filter (range / month / quarter / year +
  // relative presets). Users can still fall back to a plain value list via the
  // right-click menu ("Value list").
  if (isDate && mode === 'calendar') {
    return (
      <div className={`relative ${enforceLock ? 'pointer-events-none opacity-95' : ''}`} onContextMenu={e => { e.preventDefault(); e.stopPropagation(); setCtxMenu({ x: e.clientX, y: e.clientY }); }}>
        <DateSlicerControl dimension={dimension} label={label} selected={selected} onChange={onChange} fiscalStartMonth={fiscalStartMonth} />
        {ctxMenu && createPortal(
          <>
            <div className="fixed inset-0 z-[100]" onClick={() => setCtxMenu(null)} onContextMenu={e => { e.preventDefault(); setCtxMenu(null); }} />
            <div className="fixed glass-card rounded-lg shadow-2xl border border-border/50 py-1 w-44 z-[101] max-h-[70vh] overflow-auto" style={menuPos(ctxMenu)}>
              <p className="px-3 py-1 text-[8px] text-muted-foreground uppercase tracking-wider">Display As</p>
              <button onClick={() => { setModeOverride('calendar'); if (onDisplayModeChange) onDisplayModeChange(dimension, 'calendar'); setCtxMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs bg-primary/10 text-primary font-medium"><Check className="w-3 h-3" />Calendar</button>
              <button onClick={() => { setModeOverride('dropdown'); if (onDisplayModeChange) onDisplayModeChange(dimension, 'dropdown'); onChange(null); setCtxMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted/30"><span className="w-3" />Value list</button>
              {onFieldProperties && <button onClick={() => { setCtxMenu(null); onFieldProperties(dimension); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted/30" data-testid={`slicer-field-properties-${dimension}`}><SlidersHorizontal className="w-3 h-3" />Field Properties…</button>}
              {onOpenSettings && <><div className="h-px bg-border/40 my-1" /><button onClick={() => { setCtxMenu(null); onOpenSettings(dimension); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted/30" data-testid={`slicer-open-settings-${dimension}`}><Settings2 className="w-3 h-3" />Slicer Settings…</button></>}
            </div>
          </>,
          document.body
        )}
      </div>
    );
  }

  const changeMode = (m) => {
    setModeOverride(m);
    if (onDisplayModeChange) onDisplayModeChange(dimension, m);
    setCtxMenu(null);
    // Reset selections when switching modes
    if (m === 'multi') { setMultiSelected(selected ? [selected] : []); }
  };

  const handleMultiToggle = (val) => {
    const next = multiSelected.includes(val) ? multiSelected.filter(v => v !== val) : [...multiSelected, val];
    setMultiSelected(next);
    onChange(next.length === 0 ? null : next.length === 1 ? next[0] : next);
  };

  const filteredValues = searchTerm ? values.filter(v => String(v).toLowerCase().includes(searchTerm.toLowerCase())) : values;

  return (
    <div className={`glass-card-light rounded-xl p-2.5 min-w-[160px] relative ${enforceLock ? 'pointer-events-none opacity-95' : ''}`} data-testid={`slicer-${dimension}`}
      onContextMenu={e => { e.preventDefault(); e.stopPropagation(); setCtxMenu({ x: e.clientX, y: e.clientY }); }}>
      <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1">{locked && <Lock className="w-2.5 h-2.5" data-testid={`slicer-locked-${dimension}`} />}{label}</p>

      {/* Dropdown (default) */}
      {mode === 'dropdown' && (
        <Select value={selected || '_all_'} onValueChange={v => onChange(v === '_all_' ? null : v)} onOpenChange={o => { if (!o) setSearchTerm(''); }}>
          <SelectTrigger className="h-7 text-[10px] rounded-lg bg-muted/30 border-border/30"><SelectValue /></SelectTrigger>
          <SelectContent>
            {values.length > 8 && (
              <div className="px-1.5 pt-1 pb-1.5 sticky top-0 z-10 bg-popover border-b border-border/30">
                <div className="relative">
                  <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    onKeyDown={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}
                    placeholder="Search..." className="h-7 text-[10px] rounded-lg bg-muted/30 border-border/30 pl-7"
                    data-testid={`slicer-search-${dimension}`} />
                </div>
              </div>
            )}
            {!searchTerm && effShowAll && <SelectItem value="_all_">All ({values.length})</SelectItem>}
            {filteredValues.map(v => <SelectItem key={String(v)} value={String(v)} data-testid={`slicer-value-${dimension}-${v}`}>{String(v)}</SelectItem>)}
            {filteredValues.length === 0 && <div className="px-2 py-2 text-[9px] text-muted-foreground/70 text-center">No matches</div>}
          </SelectContent>
        </Select>
      )}

      {/* Multi-Select */}
      {mode === 'multi' && (
        <div className="relative">
          <button onClick={() => { setMultiOpen(!multiOpen); if (multiOpen) setSearchTerm(''); }} className="w-full flex items-center justify-between h-7 text-[10px] rounded-lg bg-muted/30 border border-border/30 px-2">
            <span className="truncate">{multiSelected.length === 0 ? `All (${values.length})` : `${multiSelected.length} selected`}</span>
            <ChevronDown className="w-3 h-3 text-muted-foreground" />
          </button>
          {multiOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => { setMultiOpen(false); setSearchTerm(''); }} />
              <div className="absolute top-full left-0 mt-1 w-full glass-card rounded-lg shadow-xl border border-border/50 py-1 z-50 max-h-48 overflow-auto">
                {values.length > 8 && (
                  <div className="px-1.5 pt-0.5 pb-1.5 sticky top-0 z-10 bg-popover border-b border-border/30">
                    <div className="relative">
                      <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                        onKeyDown={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}
                        placeholder="Search..." className="h-7 text-[10px] rounded-lg bg-muted/30 border-border/30 pl-7"
                        data-testid={`slicer-search-${dimension}`} />
                    </div>
                  </div>
                )}
                {!searchTerm && effShowAll && (
                  <button onClick={() => { setMultiSelected([]); onChange(null); }} className="w-full flex items-center gap-2 px-2 py-1 text-[10px] hover:bg-muted/30">
                    {multiSelected.length === 0 && <Check className="w-3 h-3 text-primary" />}
                    {multiSelected.length > 0 && <span className="w-3" />}
                    All ({values.length})
                  </button>
                )}
                {filteredValues.map(v => (
                  <button key={String(v)} onClick={() => handleMultiToggle(String(v))} data-testid={`slicer-value-${dimension}-${v}`} className="w-full flex items-center gap-2 px-2 py-1 text-[10px] hover:bg-muted/30">
                    {multiSelected.includes(String(v)) ? <Check className="w-3 h-3 text-primary" /> : <span className="w-3" />}
                    {String(v)}
                  </button>
                ))}
                {filteredValues.length === 0 && <div className="px-2 py-2 text-[9px] text-muted-foreground/70 text-center">No matches</div>}
              </div>
            </>
          )}
          {multiSelected.length > 0 && (
            <div className="flex flex-wrap gap-0.5 mt-1">
              {multiSelected.map(v => (
                <Badge key={v} className="text-[8px] bg-primary/15 text-primary border-primary/20 gap-0.5 cursor-pointer" onClick={() => handleMultiToggle(v)}>{v} <X className="w-2 h-2" /></Badge>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Buttons */}
      {mode === 'buttons' && (
        <div className="flex flex-wrap gap-1">
          {effShowAll && <button onClick={() => onChange(null)} className={'px-2 py-1 rounded-md text-[9px] transition-all ' + (!selected ? 'bg-primary text-primary-foreground font-semibold' : 'bg-muted/30 text-muted-foreground hover:bg-muted/50')}>All</button>}
          {values.map(v => (
            <button key={String(v)} onClick={() => onChange(selected === String(v) ? null : String(v))} className={'px-2 py-1 rounded-md text-[9px] transition-all ' + (selected === String(v) ? 'bg-primary text-primary-foreground font-semibold' : 'bg-muted/30 text-muted-foreground hover:bg-muted/50')}>{String(v)}</button>
          ))}
        </div>
      )}

      {/* Radio List */}
      {mode === 'radio' && (
        <div className="space-y-0.5 max-h-32 overflow-auto">
          {effShowAll && (
          <label className="flex items-center gap-1.5 text-[10px] cursor-pointer hover:bg-muted/20 px-1 py-0.5 rounded">
            <input type="radio" name={`slicer-${dimension}`} checked={!selected} onChange={() => onChange(null)} className="w-3 h-3 accent-primary" />
            All ({values.length})
          </label>
          )}
          {values.map(v => (
            <label key={String(v)} className="flex items-center gap-1.5 text-[10px] cursor-pointer hover:bg-muted/20 px-1 py-0.5 rounded">
              <input type="radio" name={`slicer-${dimension}`} checked={selected === String(v)} onChange={() => onChange(String(v))} className="w-3 h-3 accent-primary" />
              {String(v)}
            </label>
          ))}
        </div>
      )}

      {/* Searchable */}
      {mode === 'search' && (
        <div>
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search..." className="h-7 text-[10px] rounded-lg bg-muted/30 border-border/30 pl-7" />
          </div>
          <div className="mt-1 max-h-28 overflow-auto space-y-0.5">
            {!searchTerm && effShowAll && <button onClick={() => onChange(null)} className={'w-full text-left px-2 py-0.5 rounded text-[10px] ' + (!selected ? 'bg-primary/20 text-primary font-semibold' : 'hover:bg-muted/30 text-muted-foreground')}>All ({values.length})</button>}
            {filteredValues.map(v => (
              <button key={String(v)} onClick={() => onChange(selected === String(v) ? null : String(v))} className={'w-full text-left px-2 py-0.5 rounded text-[10px] ' + (selected === String(v) ? 'bg-primary/20 text-primary font-semibold' : 'hover:bg-muted/30 text-foreground')}>{String(v)}</button>
            ))}
            {filteredValues.length === 0 && <p className="text-[9px] text-muted-foreground/70 text-center py-2">No matches</p>}
          </div>
        </div>
      )}

      {/* Text filter — operator-based string match (contains / starts-with / ends-with). Value: {tk:'text', op, value}. */}
      {mode === 'text_match' && (() => {
        const tv = (selected && typeof selected === 'object' && selected.tk === 'text') ? selected : { tk: 'text', op: 'contains', value: '' };
        const emit = (op, value) => onChange((value && String(value).length) ? { tk: 'text', op, value: String(value) } : null);
        const TEXT_OPS = [
          { key: 'contains', label: 'Contains' },
          { key: 'not_contains', label: 'Does not contain' },
          { key: 'starts_with', label: 'Starts with (first chars)' },
          { key: 'ends_with', label: 'Ends with (last chars)' },
          { key: 'equals', label: 'Equals' },
        ];
        return (
          <div className="space-y-1" data-testid={`slicer-textmatch-${dimension}`}>
            <Select value={tv.op} onValueChange={op => emit(op, tv.value)}>
              <SelectTrigger className="h-7 text-[10px] rounded-lg bg-muted/30 border-border/30" data-testid={`slicer-textop-${dimension}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {TEXT_OPS.map(o => <SelectItem key={o.key} value={o.key} className="text-[10px]">{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={tv.value || ''} onChange={e => emit(tv.op, e.target.value)}
              placeholder="Enter text..." className="h-7 text-[10px] rounded-lg bg-muted/30 border-border/30"
              data-testid={`slicer-textvalue-${dimension}`} />
          </div>
        );
      })()}

      {/* Date Picker */}
      {mode === 'date_picker' && (
        <div>
          <Input type="date" value={selected || ''} onChange={e => onChange(e.target.value || null)} className="h-7 text-[10px] rounded-lg bg-muted/30 border-border/30" />
        </div>
      )}

      {/* Date Range */}
      {mode === 'date_range' && (
        <div className="space-y-1.5">
          <div className="flex gap-1 flex-wrap">
            {[
              { label: 'Last 7 days', fn: () => { const e = new Date(); const s = new Date(e - 7*86400000); return `${s.toISOString().split('T')[0]}|${e.toISOString().split('T')[0]}`; } },
              { label: 'Last 30 days', fn: () => { const e = new Date(); const s = new Date(e - 30*86400000); return `${s.toISOString().split('T')[0]}|${e.toISOString().split('T')[0]}`; } },
              { label: 'This month', fn: () => { const n = new Date(); const s = new Date(n.getFullYear(), n.getMonth(), 1); return `${s.toISOString().split('T')[0]}|${n.toISOString().split('T')[0]}`; } },
              { label: 'Last month', fn: () => { const n = new Date(); const s = new Date(n.getFullYear(), n.getMonth()-1, 1); const e = new Date(n.getFullYear(), n.getMonth(), 0); return `${s.toISOString().split('T')[0]}|${e.toISOString().split('T')[0]}`; } },
              { label: 'This quarter', fn: () => { const n = new Date(); const q = Math.floor(n.getMonth()/3); const s = new Date(n.getFullYear(), q*3, 1); return `${s.toISOString().split('T')[0]}|${n.toISOString().split('T')[0]}`; } },
              { label: 'This year', fn: () => { const n = new Date(); const s = new Date(n.getFullYear(), 0, 1); return `${s.toISOString().split('T')[0]}|${n.toISOString().split('T')[0]}`; } },
              { label: 'Last year', fn: () => { const n = new Date(); const s = new Date(n.getFullYear()-1, 0, 1); const e = new Date(n.getFullYear()-1, 11, 31); return `${s.toISOString().split('T')[0]}|${e.toISOString().split('T')[0]}`; } },
            ].map(preset => (
              <button key={preset.label} onClick={() => onChange(preset.fn())} className="text-[8px] px-1.5 py-0.5 rounded border border-border/30 text-muted-foreground hover:text-primary hover:border-primary/30 transition-colors">{preset.label}</button>
            ))}
          </div>
          <div className="flex gap-1 items-center">
            <Input type="date" value={(selected || '').split('|')[0] || ''} onChange={e => { const end = (selected || '').split('|')[1] || ''; onChange(e.target.value + '|' + end); }} className="h-7 text-[10px] rounded-lg bg-muted/30 border-border/30 flex-1" />
            <span className="text-[8px] text-muted-foreground">to</span>
            <Input type="date" value={(selected || '').split('|')[1] || ''} onChange={e => { const start = (selected || '').split('|')[0] || ''; onChange(start + '|' + e.target.value); }} className="h-7 text-[10px] rounded-lg bg-muted/30 border-border/30 flex-1" />
          </div>
        </div>
      )}

      {/* Number Range — two bounds, open-ended allowed. Value: {nk:'num', min, max}. */}
      {mode === 'number_range' && (
        <div className="flex gap-1 items-center" data-testid={`slicer-numrange-${dimension}`}>
          <Input type="number" placeholder="Min" value={isNumRangeVal && selected.min != null ? selected.min : ''}
            onChange={e => { const cur = isNumRangeVal ? selected : { nk: 'num' }; const raw = e.target.value; const next = { nk: 'num', min: raw === '' ? undefined : Number(raw), max: cur.max }; onChange((next.min == null && next.max == null) ? null : next); }}
            className="h-7 text-[10px] rounded-lg bg-muted/30 border-border/30 flex-1" />
          <span className="text-[8px] text-muted-foreground">to</span>
          <Input type="number" placeholder="Max" value={isNumRangeVal && selected.max != null ? selected.max : ''}
            onChange={e => { const cur = isNumRangeVal ? selected : { nk: 'num' }; const raw = e.target.value; const next = { nk: 'num', min: cur.min, max: raw === '' ? undefined : Number(raw) }; onChange((next.min == null && next.max == null) ? null : next); }}
            className="h-7 text-[10px] rounded-lg bg-muted/30 border-border/30 flex-1" />
        </div>
      )}

      {/* Right-click context menu */}
      {ctxMenu && createPortal(
        <>
          <div className="fixed inset-0 z-[100]" onClick={() => setCtxMenu(null)} onContextMenu={e => { e.preventDefault(); setCtxMenu(null); }} />
          <div className="fixed glass-card rounded-lg shadow-2xl border border-border/50 py-1 w-44 z-[101] max-h-[70vh] overflow-auto" style={menuPos(ctxMenu)}>
            <p className="px-3 py-1 text-[8px] text-muted-foreground uppercase tracking-wider">Display As</p>
            {DISPLAY_MODES.map(dm => (
              <button key={dm.key} onClick={() => changeMode(dm.key)} className={'w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-colors ' + (mode === dm.key ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-muted/30')}>
                {mode === dm.key && <Check className="w-3 h-3" />}
                {mode !== dm.key && <span className="w-3" />}
                {dm.label}
              </button>
            ))}
            {onFieldProperties && <><div className="h-px bg-border/40 my-1" /><button onClick={() => { setCtxMenu(null); onFieldProperties(dimension); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted/30" data-testid={`slicer-field-properties-${dimension}`}><SlidersHorizontal className="w-3 h-3" />Field Properties…</button></>}
            {onOpenSettings && <><div className="h-px bg-border/40 my-1" /><button onClick={() => { setCtxMenu(null); onOpenSettings(dimension); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted/30" data-testid={`slicer-open-settings-${dimension}`}><Settings2 className="w-3 h-3" />Slicer Settings…</button></>}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

export function ConditionalCell({ value, rules, isNumeric }) {
  let style = {};
  if (isNumeric && rules.length > 0) {
    for (const rule of rules) {
      if (rule.operator === 'gt' && value > rule.threshold) style = { color: rule.color, fontWeight: 600 };
      else if (rule.operator === 'lt' && value < rule.threshold) style = { color: rule.color, fontWeight: 600 };
      else if (rule.operator === 'gte' && value >= rule.threshold) style = { color: rule.color, fontWeight: 600 };
    }
  }
  return <span style={style}>{isNumeric && typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(value ?? '')}</span>;
}
